import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Check, Loader2, Info } from "lucide-react";
import { firmApi } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";

const inputClasses =
  "w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm";

const disabledInputClasses =
  "w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-500 outline-none text-sm cursor-not-allowed";

const labelClasses = "block text-sm font-medium text-slate-700 mb-1.5";

const FirmProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { firmData, refreshUserData } = useUserDataContext();

  // Parse the structured address from firmData
  const addr =
    firmData?.address && typeof firmData.address === "object"
      ? firmData.address
      : { line1: "", city: "", region: "", postal: "", country: "" };

  const jurisdictionCountry = firmData?.jurisdiction?.country || "CA";
  const isUS = jurisdictionCountry === "US";

  const [name, setName] = useState(firmData?.name || "");
  const [primaryPhone, setPrimaryPhone] = useState(firmData?.primaryPhone || "");
  const [line1, setLine1] = useState(addr.line1 || "");
  const [city, setCity] = useState(addr.city || "");
  const [region, setRegion] = useState(addr.region || "");
  const [postal, setPostal] = useState(addr.postal || "");

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (firmData) {
      setName(firmData.name || "");
      setPrimaryPhone(firmData.primaryPhone || "");
      const a =
        firmData.address && typeof firmData.address === "object"
          ? firmData.address
          : { line1: "", city: "", region: "", postal: "", country: "" };
      setLine1(a.line1 || "");
      setCity(a.city || "");
      setRegion(a.region || "");
      setPostal(a.postal || "");
    }
  }, [firmData]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await firmApi.updateProfile({
        name: name.trim(),
        primaryPhone: primaryPhone.trim() || null,
        address: {
          line1: line1.trim() || null,
          city: city.trim() || null,
          region: region.trim() || null,
          postal: postal.trim() || null,
          country: jurisdictionCountry,
        },
      });
      await refreshUserData();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Failed to save firm profile:", err);
    } finally {
      setSaving(false);
    }
  };

  const countryLabel = isUS ? "United States" : "Canada";
  const regionLabel = isUS ? "State" : "Province";
  const postalLabel = isUS ? "ZIP Code" : "Postal Code";
  const primaryEmail = firmData?.primaryEmail || firmData?.email || "";

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors mb-3"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Firm Details</h1>
            <p className="text-sm text-slate-500 mt-1">
              Name, address, jurisdiction, and contact info
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white transition-all hover:shadow-md disabled:opacity-50"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : saved ? (
              <>
                <Check className="h-4 w-4" />
                Saved
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>

      <div className="space-y-8 max-w-2xl">
        {/* Firm Name & Contact */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-5 w-5" style={{ color: "var(--color-primary)" }} />
            <h3 className="text-base font-semibold text-slate-800">General Information</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className={labelClasses}>
                Firm Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setSaved(false);
                }}
                className={inputClasses}
                placeholder="Your law firm name"
              />
            </div>

            <div>
              <label className={labelClasses}>Primary Email</label>
              <input
                type="email"
                value={primaryEmail}
                disabled
                className={disabledInputClasses}
              />
              <p className="text-xs text-slate-400 mt-1">
                Set during signup. Contact support to change.
              </p>
            </div>

            <div>
              <label className={labelClasses}>Primary Phone</label>
              <input
                type="text"
                value={primaryPhone}
                onChange={(e) => {
                  setPrimaryPhone(e.target.value);
                  setSaved(false);
                }}
                className={inputClasses}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-5 w-5" style={{ color: "var(--color-primary)" }} />
            <h3 className="text-base font-semibold text-slate-800">Address</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className={labelClasses}>Street Address</label>
              <input
                type="text"
                value={line1}
                onChange={(e) => {
                  setLine1(e.target.value);
                  setSaved(false);
                }}
                className={inputClasses}
                placeholder="123 Main Street, Suite 400"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClasses}>City</label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    setSaved(false);
                  }}
                  className={inputClasses}
                  placeholder="Toronto"
                />
              </div>
              <div>
                <label className={labelClasses}>{regionLabel}</label>
                <input
                  type="text"
                  value={region}
                  onChange={(e) => {
                    setRegion(e.target.value);
                    setSaved(false);
                  }}
                  className={inputClasses}
                  placeholder={isUS ? "NY" : "ON"}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClasses}>{postalLabel}</label>
                <input
                  type="text"
                  value={postal}
                  onChange={(e) => {
                    setPostal(e.target.value);
                    setSaved(false);
                  }}
                  className={inputClasses}
                  placeholder={isUS ? "10001" : "M5V 3A8"}
                />
              </div>
              <div>
                <label className={labelClasses}>Country</label>
                <input
                  type="text"
                  value={countryLabel}
                  disabled
                  className={disabledInputClasses}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Jurisdiction */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-5 w-5" style={{ color: "var(--color-primary)" }} />
            <h3 className="text-base font-semibold text-slate-800">Jurisdiction</h3>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClasses}>Country</label>
                <input
                  type="text"
                  value={countryLabel}
                  disabled
                  className={disabledInputClasses}
                />
              </div>
              <div>
                <label className={labelClasses}>Region</label>
                <input
                  type="text"
                  value={firmData?.jurisdiction?.region || ""}
                  disabled
                  className={disabledInputClasses}
                />
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700">
                Jurisdiction is set during signup. Contact support to change.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirmProfilePage;
